import React from "react";
import { ServiceObjectTableQuery } from "../models";
import { Table, useAuth } from "../../../components";
import { getTablePageCount } from "../../../helpers/tableHelpers";
import { useGetServiceObjectsQuery } from "../queries";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import { renderDate } from "../../../helpers/dayjsHeleprs";
import { useNavigate } from "../../../helpers/navigationHelpers";
import { CPServiceObject } from "../../../api/models/unified";
import { ExtendedColumn } from "../../../components/table/Table";
import ServiceObjectNextRevisionTooltip from "./ServiceObjectNextRevisionTooltip";

interface Props {
  queryParams: ServiceObjectTableQuery;
  setQueryParams: (queryParams: ServiceObjectTableQuery) => void;
}

const ServiceObjectsTable: React.FC<Props> = (props): React.ReactElement => {
  const { queryParams, setQueryParams } = props;
  const { tServiceObject, t } = useCustomTranslation();
  const navigate = useNavigate();

  const { user } = useAuth();

  const query = useGetServiceObjectsQuery(queryParams);

  const columns: ExtendedColumn<CPServiceObject>[] = [
    {
      key: "name",
      name: tServiceObject("name"),
      sortable: true,
    },
    {
      key: "categoryName",
      name: tServiceObject("category"),
      sortable: true,
    },
    {
      key: "customerName",
      name: tServiceObject("operator_customer"),
      sortable: true,
      hidden: !user?.ownsServiceObjects,
    },
    {
      key: "owningCustomerName",
      name: tServiceObject("owning_customer"),
      sortable: true,
      hidden: !user?.hasServiceObjectsWithOwner,
    },
    {
      key: "identificationNumber",
      name: tServiceObject("identification_number"),
      sortable: true,
    },
    {
      key: "count",
      name: tServiceObject("count"),
      sortable: true,
    },
    {
      key: "manufacturerName",
      name: tServiceObject("manufacturer"),
      sortable: true,
    },
    {
      key: "address",
      name: tServiceObject("address"),
      renderCell: ({ row }) => row.oneLinerAddress,
      sortable: true,
    },
    {
      key: "location",
      name: tServiceObject("location"),
      sortable: true,
    },
    {
      key: "nextRevision",
      name: tServiceObject("nearest_next_revision"),
      renderCell: ({ row }) => (
        <ServiceObjectNextRevisionTooltip serviceObject={row} />
      ),
      sortable: true,
    },
    {
      key: "warrantyTill",
      name: tServiceObject("warranty_till"),
      renderCell: ({ row }) => renderDate(row.warrantyTill),
      sortable: true,
    },
    {
      key: "isActive",
      name: tServiceObject("state"),
      renderCell: ({ row }) => (row.isActive ? t("active") : t("inactive")),
      sortable: true,
    },
  ];

  return (
    <Table
      columns={columns}
      data={query.data?.data ?? []}
      loading={query.isLoading}
      queryParams={queryParams}
      onTableQueryChange={setQueryParams}
      pageCount={getTablePageCount(
        query.data?.total ?? 1,
        queryParams.pageSize,
      )}
      onRowClick={(item) => navigate.goTo(`serviceObjects/${item.id}`)}
      disableExtendToFullWidth
    />
  );
};

export default ServiceObjectsTable;
